import { Upload as AntdUpload, UploadProps } from "antd";
import { useEffect } from "react";
import { UPLOAD_URL } from "../../model/constants";
import { itemRenderer } from "./item-render";
import { UploadButton } from "./upload-button";
import { useForceRender } from "../../utils/use-force-render";
import { limit } from "../../utils/limit";
import { extractRef } from "../../utils/extract-ref";
import { notNullable } from "../../utils/not-nullable";
import { useFileList } from "../../model/use-file-list";
import { UploadResponseFile } from "@/shared/form/upload/model/types";
import { UploadChangeParam } from "antd/es/upload";
import { UploadFile } from "antd/lib";
import { useErrorHandler } from "@/shared/form/upload/utils/use-error-handler";

export type AttachmentsProps = Omit<UploadProps, "value"> & {
  value?: string[];
  onChange?: (value: string[]) => void;
  afterUpload?: (file: UploadFile<UploadResponseFile>) => void;
  onStartUploading?: (file: UploadFile<UploadResponseFile>) => void;
  onFinishUploading?: (file: UploadFile<UploadResponseFile>) => void;
};

export const Attachments = ({
  onChange,
  value,
  children,
  maxCount,
  disabled,
  afterUpload,
  onStartUploading,
  onFinishUploading,
  ...rest
}: AttachmentsProps) => {
  const { counter, rerender } = useForceRender();
  const { fileList, setFileList } = useFileList({ value });
  const errorHandler = useErrorHandler();

  useEffect(() => {
    if (counter === 0) {
      return;
    }

    const a = fileList.map(extractRef).filter(notNullable);

    onChange?.(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  return (
    <AntdUpload
      name="file"
      action={UPLOAD_URL}
      fileList={fileList}
      onChange={(info: UploadChangeParam<UploadFile<UploadResponseFile>>) => {
        errorHandler(info);

        setFileList((prevFileList) => {
          let newFileList = prevFileList.filter(
            (file) => file.uid !== info.file.uid,
          );

          if (info.file.status === "error") {
            return newFileList;
          }

          if (info.file.status === "uploading" || info.file.status === "done") {
            newFileList = [...newFileList, info.file];
          }

          newFileList = limit(newFileList, maxCount);

          return newFileList;
        });

        if (info.file.status === "uploading" && onStartUploading) {
          onStartUploading(info.file);
        }

        if (info.file.status === "done" && afterUpload) {
          afterUpload(info.file);
        }

        if (
          (info.file.status === "done" || info.file.status === "error") &&
          onFinishUploading
        ) {
          onFinishUploading(info.file);
        }

        if (info.file.status === "done" || info.file.status === "removed") {
          rerender();
        }
      }}
      itemRender={itemRenderer({ disabled })}
      disabled={disabled}
      {...rest}
    >
      {children ?? <UploadButton disabled={disabled} />}
    </AntdUpload>
  );
};
